import { Component } from '@theme/component';

/**
 * Progressively enhances the email signup `{% form 'customer' %}` so a
 * successful submission reveals the (already server-rendered, but hidden)
 * success message in place, instead of navigating away from the page.
 *
 * Submits via `fetch` with an `X-Requested-With: XMLHttpRequest` header -
 * matching the header Shopify's own storefront AJAX requests use - which is
 * required for Shopify's `/contact` (customer signup) endpoint to accept a
 * background request at all; without it the endpoint rejects the request
 * outright with a generic 400 "invalid parameters" error regardless of the
 * submitted data.
 *
 * Success is decided purely from the HTTP status (`response.ok`) - the same
 * signal the store's previous, proven implementation relied on. Anything
 * other than a 2xx response (a validation error, a reCAPTCHA/rate-limit
 * challenge, or the request failing outright) falls back to a real,
 * full-page form submission, letting Shopify handle it exactly the way it
 * normally would - including showing/solving any challenge it needs to, via
 * an actual page navigation/redirect.
 *
 * @typedef {Object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} successMessage
 *
 * @extends {Component<Refs>}
 */
class EmailSignupForm extends Component {
  requiredRefs = ['form', 'input', 'submitButton', 'successMessage'];

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * Set right before calling `requestSubmit()` to let a genuine fallback
   * submission (triggered by this component itself) pass straight through
   * on its next `handleSubmit` call, instead of being intercepted again.
   * @type {boolean}
   */
  #bypassNextSubmit = false;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Handles the form's submit event.
   *
   * @param {Event} event
   */
  handleSubmit(event) {
    if (this.#bypassNextSubmit) {
      this.#bypassNextSubmit = false;
      return;
    }

    event.preventDefault();

    const { form, submitButton } = this.refs;
    const formData = new FormData(form);

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    submitButton.disabled = true;

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json, text/javascript, */*; q=0.01',
      },
      signal: abortController.signal,
    })
      .then((response) => this.#handleResponse(response, form))
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        this.#submitNatively(form);
      })
      .finally(() => {
        submitButton.disabled = false;
      });
  }

  /**
   * Decides whether the background submission succeeded, trusting the HTTP
   * status alone (matching the proven legacy implementation this replaces -
   * it used the same `response.ok` check) instead of trying to parse/match
   * the response body: Shopify's AJAX "Accept: application/json" mode
   * doesn't always return the clean, consistently-shaped JSON object a
   * stricter body check would need, which was causing genuine successes to
   * be misclassified as failures and silently re-submitted for real.
   *
   * A non-2xx status (a real validation error, a reCAPTCHA/rate-limit
   * challenge, etc.) falls back to a real, full-page submission instead,
   * letting Shopify handle it exactly the way it normally would.
   *
   * @param {Response} response
   * @param {HTMLFormElement} form
   */
  #handleResponse(response, form) {
    if (response.ok) {
      this.#showSuccess();
      return;
    }

    this.#submitNatively(form);
  }

  #showSuccess() {
    const { input, successMessage } = this.refs;
    successMessage.hidden = false;
    successMessage.focus();
    input.value = '';
  }

  /**
   * Falls back to a genuine, non-AJAX form submission.
   *
   * Uses `requestSubmit()` (not `submit()`) so the `submit` event actually
   * fires - matching a real user-initiated submit as closely as possible,
   * e.g. for any store-wide spam-protection script that also listens for it
   * - while `#bypassNextSubmit` stops this component from intercepting that
   * follow-up event itself.
   *
   * @param {HTMLFormElement} form
   */
  #submitNatively(form) {
    this.#bypassNextSubmit = true;
    form.requestSubmit();
  }
}

if (!customElements.get('email-signup-form')) {
  customElements.define('email-signup-form', EmailSignupForm);
}
